import { useCallback, useEffect, useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import { buildReportData } from './tokens/normalizers';
import { EMPTY_DATA } from './tokens/loadFixtures';
import type { ReportData } from './tokens/types';
import { getSingleCompanyDetailsService, fetchAmlPepData, fetchOrgInfoService, getMcaDocApiData } from '../../store/investor/service';
import {
    getCompanyOverviewData,
    getCompanyOverviewDataV2,
    getShareholdingPatternData,
    fetchFundingHistoryDataService,
    getManagementProfileUserData,
} from '../../store/dashboard/overview/service';
import { getAicaGradeDataService } from '../../store/dashboard/service';
import { getDirectorsOverviewData } from '../../store/dashboard/creditBureau/service';
import { fetchRelatedPartyDataService } from '../../store/dashboard/relatedParties/relatedParties_service';
import {
    fetchBankStatementFromCashflowService,
    fetchFinancialMetricsServicePolus,
} from '../../store/dashboard/financials/financialsServices';
import { fetchBankStatementDataService } from '../../store/dashboard/bankStatement/service';
import { getGraphData, getHeadlineData } from '../../store/dashboard/service';
import { getLegalCasesDataService } from '../../store/dashboard/tables/service';
import { getStartDateEndDate, getTimeStamp } from '../../utils/dateUtils';
import { fetchAicaOverviewData } from '../../store/company/service';
import { fetchAlerts } from '../../store/dashboard/AlertEngine/service';
import { pickData, pickError, fetchStatementSlices } from './useAicaReportData.helpers';
import { OVERVIEW_METRICS, INSIGHT_MODULE_IDS } from './useAicaReportData.types';
import type { FetchStatus, SourceKey, UseAicaReportData } from './useAicaReportData.types';

export type { FetchStatus, UseAicaReportData } from './useAicaReportData.types';

type TaskSpec = {
    key: SourceKey;
    run: () => Promise<any>;
    // When true, store the full settled value rather than `.data` — for helpers that already return the final shape.
    raw?: boolean;
};

const graphParams = (graphNames: string[], tabName: string, reportId?: string) => ({
    graphNames,
    tabName,
    startDate: '',
    endDate: '',
    tenure: 'Monthly',
    reportId,
});

function buildTasks(args: { assesseeOrgId: string; investorOrgId: string; partnerUserId: string }): TaskSpec[] {
    const { assesseeOrgId, investorOrgId, partnerUserId } = args;

    const insightTasks: TaskSpec[] = INSIGHT_MODULE_IDS.map(({ moduleId, key }) => ({
        key,
        run: () => fetchAicaOverviewData({ orgId: assesseeOrgId, moduleId }),
    }));

    return [
        { key: 'assesseeBasic', run: () => getSingleCompanyDetailsService({ assesseeOrgId }) },
        { key: 'unifiedOverview', run: () => getCompanyOverviewDataV2({ assesseeOrgId }) },
        { key: 'score', run: () => getAicaGradeDataService({ assesseeOrgId, investorOrgId }) },
        { key: 'mainOverview', run: () => getCompanyOverviewData({ assesseeOrgId, investorOrgId, partnerUserId }) },
        {
            key: 'relatedParties',
            run: () =>
                fetchRelatedPartyDataService({
                    orgId: assesseeOrgId,
                    taskStepsList: ['AION_INGEST_RELATED_PARTIES_DATA'],
                }),
        },
        { key: 'shareholding', run: () => getShareholdingPatternData(graphParams(['shareholdingPattern'], 'overview')) },
        {
            key: 'fundingHistory',
            run: () =>
                fetchFundingHistoryDataService({
                    orgId: assesseeOrgId,
                    assesseeOrgId,
                    taskStepsList: ['AION_INGEST_FUNDING_HISTORY'],
                }),
        },
        { key: 'mcaDocs', run: () => getMcaDocApiData({ orgId: assesseeOrgId }) },
        // compliance graphs are scoped to the assessee (live session) on Polus; passing an unrelated audit-reportId filters them out server-side.
        { key: 'gstr1', run: () => getGraphData(graphParams(['gstr1FilingStatus'], 'compliances')) },
        { key: 'gstr3b', run: () => getGraphData(graphParams(['gstr3bFilingStatus'], 'compliances')) },
        { key: 'epf', run: () => getGraphData(graphParams(['epfCompliance'], 'compliances')) },
        {
            key: 'compliancesHeadline',
            run: () =>
                getHeadlineData({
                    tabName: 'compliances',
                    startDate: '',
                    endDate: '',
                    tenure: 'Monthly',
                }),
        },
        {
            key: 'auditedFinancials',
            run: () =>
                fetchFinancialMetricsServicePolus({
                    orgId: assesseeOrgId,
                    startDate: getStartDateEndDate().startDate,
                    endDate: null,
                    frequency: 'Yearly',
                    metricsList: OVERVIEW_METRICS,
                    round: true,
                    type: 'auditedfinancials',
                }),
        },
        { key: 'legalCases', run: () => getLegalCasesDataService({ assesseeOrgId, requestId: getTimeStamp() }) },
        { key: 'amlPep', run: () => fetchAmlPepData({ orgId: assesseeOrgId }) },
        {
            key: 'alerts',
            run: () =>
                fetchAlerts({
                    assesseOrgId: assesseeOrgId,
                    parentOrgId: investorOrgId,
                    pageNumber: 1,
                    pageSize: 30,
                    alertTypes: ['ALERT', 'EWS_ALERT'],
                }),
        },
        { key: 'orgInfo', run: () => fetchOrgInfoService({ orgId: assesseeOrgId }) },
        ...insightTasks,
        {
            key: 'auditedStatements',
            run: () => fetchStatementSlices(assesseeOrgId, 'AicaAuditedFinancials', 'auditedfinancials'),
            raw: true,
        },
        { key: 'mcaCharges', run: () => getGraphData(graphParams(['mcaCharges'], 'compliances')) },
        // Provisional statements — same shape as audited; rendered as a fallback
        // inside the Audited module when audited slices aren't available.
        {
            key: 'provisionalStatements',
            run: () => fetchStatementSlices(assesseeOrgId, 'AicaFinancials', 'provisionalstatements', 'mappedData'),
            raw: true,
        },
        { key: 'bankingCashflow', run: () => fetchBankStatementFromCashflowService({}) },
        { key: 'bankingOverview', run: () => fetchBankStatementDataService() },
    ];
}

async function fetchDirectorsDeep(assesseeOrgId: string, mainOverviewData: any) {
    let overviewList: any[] = [];
    try {
        const overviewListRes: any = await getDirectorsOverviewData({ orgId: assesseeOrgId });
        overviewList = overviewListRes?.data?.responseBody?.combinedDirectors ?? [];
    } catch {
        overviewList = [];
    }
    const fallbackList: any[] = overviewList.length === 0 ? mainOverviewData?.data?.managementProfile?.profiles ?? [] : [];
    const seedList = overviewList.length > 0 ? overviewList : fallbackList;
    const detailsResults = await Promise.allSettled(
        seedList
            .slice(0, 10)
            .map((d: any) =>
                d?.directorId ? getManagementProfileUserData({ orgId: assesseeOrgId, directorId: d.directorId }) : Promise.resolve(null),
            ),
    );
    return {
        overviewList,
        detailsList: detailsResults.map(pickData),
    };
}

type LoadResult = {
    rawByKey: Record<SourceKey, any>;
    errors: Partial<Record<SourceKey, string>>;
    allRejected: boolean;
};

async function loadReportData(args: { assesseeOrgId: string; investorOrgId: string; partnerUserId: string }): Promise<LoadResult> {
    const tasks = buildTasks(args);
    const settled = await Promise.allSettled(tasks.map((t) => t.run()));

    const errors: Partial<Record<SourceKey, string>> = {};
    const rawByKey: Partial<Record<SourceKey, any>> = {};
    settled.forEach((r, i) => {
        const { key, raw } = tasks[i];
        const err = pickError(r);
        if (err) errors[key] = err;
        rawByKey[key] = raw ? (r.status === 'fulfilled' ? r.value : null) : pickData(r);
    });

    rawByKey.directorsDeep = await fetchDirectorsDeep(args.assesseeOrgId, rawByKey.mainOverview);

    const allRejected = settled.every((r) => r.status === 'rejected');
    return { rawByKey: rawByKey as Record<SourceKey, any>, errors, allRejected };
}

export const useAicaReportData = (assesseeOrgId: string | undefined): UseAicaReportData => {
    const userData = useAppSelector((state) => state.user.userData);
    const investorOrgId = userData.investorOrgId;
    const partnerUserId = (userData as any).userId;

    const [status, setStatus] = useState<FetchStatus>(assesseeOrgId ? 'idle' : 'ready');
    const [data, setData] = useState<ReportData>(EMPTY_DATA);
    const [errors, setErrors] = useState<Partial<Record<SourceKey, string>>>({});
    const [tick, setTick] = useState(0);

    useEffect(() => {
        if (!assesseeOrgId) {
            setData(EMPTY_DATA);
            setStatus('ready');
            setErrors({});
            return;
        }

        let cancelled = false;
        setData(EMPTY_DATA);
        setStatus('loading');
        setErrors({});

        (async () => {
            const result = await loadReportData({ assesseeOrgId, investorOrgId, partnerUserId });
            if (cancelled) return;

            setData(buildReportData(result.rawByKey as any));
            setErrors(result.errors);
            setStatus(result.allRejected ? 'error' : 'ready');
        })();

        return () => {
            cancelled = true;
        };
    }, [assesseeOrgId, investorOrgId, partnerUserId, tick]);

    const refetch = useCallback(() => setTick((t) => t + 1), []);

    return { status, data, errors, refetch };
};
